import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatDateTime, formatTimestamp } from "@/lib/format";
import { Activity, ShoppingCart, Phone, ArrowRight } from "lucide-react";
import { DriverMap } from "@/components/DriverMap";

const inr = (n) => (n == null ? "—" : "₹" + Number(n).toLocaleString("en-IN"));

export const AdminLivePanel = ({ leads = [], onChanged }) => {
  const [data, setData] = useState({ online_drivers: [], purchases: [] });
  useEffect(() => {
    const load = () => api.get("/admin/live").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => { clearInterval(t); };
  }, []);

  return (
    <section data-testid="admin-live-panel" className="grid lg:grid-cols-5 gap-4 mb-8">
      <OnlineDrivers drivers={data.online_drivers} />
      <PurchaseFeed purchases={data.purchases} />
      <DriverMap drivers={data.online_drivers} leads={leads} onChanged={onChanged} />
    </section>
  );
};

const Card = ({ testId, className, icon: Icon, title, count, children }) => (
  <div data-testid={testId} className={`bg-white rounded-2xl border border-slate-200/80 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.06)] ${className}`}>
    <div className="flex items-center justify-between">
      <h3 className="font-display font-bold text-slate-900 flex items-center gap-2"><Icon className="w-4 h-4 text-amber-500" /> {title}</h3>
      <span data-testid={`${testId}-count`} className="text-xs font-semibold rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">{count}</span>
    </div>
    <div className="mt-3 max-h-80 overflow-y-auto space-y-2 pr-1">{children}</div>
  </div>
);

const OnlineDrivers = ({ drivers }) => (
  <Card testId="admin-online-drivers" className="lg:col-span-2" icon={Activity} title="Drivers Online Now" count={drivers.length}>
    {drivers.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No drivers online right now</p>}
    {drivers.map((d) => (
      <div key={d.user_id} data-testid="admin-online-driver-row" className="flex items-center gap-3 rounded-xl border border-slate-100 px-3 py-2.5">
        <span className="relative flex w-3 h-3">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
          <span data-testid="admin-online-indicator" className="relative inline-flex w-3 h-3 rounded-full bg-emerald-500" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900 truncate">{d.name}</p>
          <p className="text-xs text-slate-500 truncate">{d.vehicle_type || "—"} · {(d.cities || []).map((c) => c.replace(/\b\w/g, (m) => m.toUpperCase())).join(", ") || "—"}</p>
        </div>
        {d.phone && <a href={`tel:+91${d.phone}`} className="text-xs text-slate-500 hover:text-amber-600 flex items-center gap-1"><Phone className="w-3 h-3" />{d.phone}</a>}
      </div>
    ))}
  </Card>
);

const PurchaseFeed = ({ purchases }) => (
  <Card testId="admin-lead-purchases" className="lg:col-span-3" icon={ShoppingCart} title="Recent Lead Purchases" count={purchases.length}>
    {purchases.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No lead purchases yet</p>}
    {purchases.map((p) => (
      <div key={p.id} data-testid="admin-purchase-row" className="rounded-xl border border-slate-100 px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-slate-900 truncate">{p.driver_name || "Driver"} <span className="font-normal text-slate-400">{p.driver_phone ? `· ${p.driver_phone}` : ""}</span></p>
          <span className="text-sm font-bold text-emerald-600 shrink-0">{inr(p.amount)}</span>
        </div>
        {p.lead_deleted ? (
          <p className="text-xs text-slate-400 mt-0.5">Lead {p.lead_id} was deleted</p>
        ) : (<>
          <p className="text-xs text-slate-700 mt-0.5 flex items-center gap-1 flex-wrap">
            {p.from_location || "—"} <ArrowRight className="w-3 h-3 text-slate-400" /> {p.to_location || "—"}
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {p.trip_type || "—"} · {p.vehicle_type || "—"} · Trip {formatDateTime(p.travel_date, p.travel_time)}{p.customer_name ? ` · ${p.customer_name}` : ""}
          </p>
        </>)}
        <p data-testid="admin-purchase-time" className="text-[11px] text-amber-600 mt-0.5">Purchased {formatTimestamp(p.purchased_at)}</p>
      </div>
    ))}
  </Card>
);
